import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { PublicNavbar } from '../components/PublicNavbar'
import { PublicFooter } from '../components/PublicFooter'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'

export const LoginPage = () => {
  const { signInWithPassword, isAuthenticated, user } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [email, setEmail] = useState(user?.email ?? '')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const returnTo = (location.state as { from?: string } | null)?.from || '/dashboard'

  useEffect(() => {
    if (isAuthenticated) {
      navigate(returnTo, { replace: true })
    }
  }, [isAuthenticated, navigate, returnTo])

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setError(null)
    setMessage(null)
    setIsSubmitting(true)
    try {
      await signInWithPassword({ email: email.trim(), password })
      navigate(returnTo, { replace: true })
    } catch (err: any) {
      setError(err?.message ?? 'Failed to sign in. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleResendConfirmation = async () => {
    setError(null)
    const { error: resendError } = await supabase.auth.resend({
      type: 'signup',
      email: email.trim(),
      options: {
        emailRedirectTo: `${window.location.origin}/login`,
      },
    })
    if (resendError) {
      setError(resendError.message)
      return
    }
    setMessage('Confirmation email sent. Please check your inbox.')
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <PublicNavbar />
      <Container
        maxWidth="sm"
        sx={{
          flexGrow: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 8,
        }}
      >
        <Card sx={{ width: '100%', maxWidth: 520 }}>
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={2}>
                <Typography variant="h4">Log in</Typography>
                <Typography color="text.secondary">
                  Sign in with your email and password to continue.
                </Typography>
                {error && <Alert severity="error">{error}</Alert>}
                {message && <Alert severity="success">{message}</Alert>}
                <TextField
                  label="Email"
                  type="email"
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                />
                <TextField
                  label="Password"
                  type="password"
                  required
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                />
                <Button variant="contained" color="warning" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Signing in...' : 'Sign in'}
                </Button>
                <Link
                  component="button"
                  type="button"
                  onClick={() => navigate('/forgot-password', { state: { email } })}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Forgot password?
                </Link>
                <Typography variant="body2" color="text.secondary">
                  Need an account?{' '}
                  <Link
                    component="button"
                    type="button"
                    onClick={() => navigate('/signup')}
                    sx={{ verticalAlign: 'baseline' }}
                  >
                    Create one
                  </Link>
                </Typography>
                {error?.toLowerCase().includes('email not confirmed') && (
                  <Link component="button" type="button" onClick={handleResendConfirmation}>
                    Resend confirmation email
                  </Link>
                )}
              </Stack>
            </Box>
          </CardContent>
        </Card>
      </Container>
      <PublicFooter />
    </Box>
  )
}
